/**
 * "Back" — the affordance a reader reaches for when they are done looking at something.
 *
 * ## Why this is not just a link to a fixed page
 *
 * The deal report is reached three ways: from the entry form, from a project, and from a pasted link.
 * A fixed destination is wrong for two of those — sending someone who came from a project back to the
 * deal-entry form is not "back", it is "start over", which is what the page offered before this.
 *
 * So: use the history when there is history, and fall back to a named destination when there is not
 * (a deep link, a fresh tab). The LABEL always names where the fallback goes, never the vague "Back",
 * because a control that cannot say where it leads is a control people hesitate to press.
 */

import { useNavigate } from 'react-router-dom';

export interface BackLinkProps {
  /** Where to go when there is no history to pop — a deep link, a new tab. */
  fallback: string;
  /** What that destination is called, e.g. "All projects". Shown as the label. */
  label: string;
}

/**
 * Whether the browser has somewhere to go back to WITHIN this app. `history.length` counts the whole
 * tab's session, so it is a heuristic rather than a fact — which is exactly why the fallback exists and
 * why the label names a real destination either way. Exported for a direct test.
 */
export function hasHistory(historyLength: number): boolean {
  return historyLength > 1;
}

export function BackLink({ fallback, label }: BackLinkProps) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="link-btn backlink"
      onClick={() => {
        if (typeof window !== 'undefined' && hasHistory(window.history.length)) {
          void navigate(-1);
          return;
        }
        void navigate(fallback);
      }}
    >
      ← {label}
    </button>
  );
}
